import React, { useState, useRef } from "react";
import api from "../../utils/apis";
import toast from "react-hot-toast";

import {
  MessageSquareText,
  FolderOpen,
  Gauge,
  FileText,
  Paperclip,
  Ticket,
  X,
  Loader2,
} from "lucide-react";

const SupportModal = ({ onClose, userData }) => {
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    priority: "medium",
    category: "",
    attachment: null,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const config = {
    title: "Submit Support Request",
    subtitle: "Our support team will get back to you within 2 business hours",
    steps: 2,
    fields: [
      [
        {
          name: "subject",
          label: "Request Title",
          type: "text",
          placeholder: "Brief summary of your issue",
          required: true,
          icon: MessageSquareText,
        },
        {
          name: "category",
          label: "Issue Category",
          type: "select",
          required: true,
          icon: FolderOpen,
          options: [
            { value: "", label: "Select category" },
            { value: "technical", label: "Technical Support" },
            { value: "billing", label: "Billing & Payments" },
            { value: "order", label: "Order Management" },
            { value: "account", label: "Account Issues" },
            { value: "feature", label: "Feature Request" },
            { value: "other", label: "Other" },
          ],
        },
        {
          name: "priority",
          label: "Priority Level",
          type: "select",
          required: true,
          icon: Gauge,
          options: [
            { value: "low", label: "Low - General inquiry" },
            { value: "medium", label: "Medium - Feature request" },
            { value: "high", label: "High - System issue" },
            { value: "urgent", label: "Urgent - System down" },
          ],
        },
      ],
      [
        {
          name: "description",
          label: "Detailed Description",
          type: "textarea",
          required: true,
          icon: FileText,
          rows: 6,
          placeholder:
            "Please provide detailed information about your issue, including:\n• Steps to reproduce\n• Expected behavior\n• Actual behavior\n• Error messages (if any)",
        },
        {
          name: "attachment",
          label: "Attach Files (Optional)",
          type: "file",
          icon: Paperclip,
          accept: ".jpg,.jpeg,.png,.pdf,.doc,.docx",
          description:
            "Screenshots, documents, or logs that might help us resolve your issue faster",
        },
      ],
    ],
  };

  const currentFields = config.fields[currentStep - 1];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!userData?.id) {
    toast.error("User not found. Please login again.");
    return;
  }

  if (!formData.subject || !formData.description || !formData.category) {
    toast.error("Please fill all required fields");
    return;
  }

  setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("user_id", userData.id);
      submitData.append("subject", formData.subject);
      submitData.append("description", formData.description);
      submitData.append("priority", formData.priority);
      submitData.append("category", formData.category);

      if (formData.attachment) {
        submitData.append("attachment", formData.attachment);
      }

      const response = await api.post("/support/tickets", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success("Support ticket submitted successfully!");
        setFormData({
          subject: "",
          description: "",
          priority: "medium",
          category: "",
          attachment: null,
        });
        setCurrentStep(1);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        onClose();
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      toast.error(
        `Error: ${
          error.response?.data?.message ||
          error.message ||
          "Failed to submit request. Please try again."
        }`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!formData.subject || !formData.category) {
      toast.error("Please complete required fields");
      return;
    }
    setCurrentStep((s) => s + 1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-3 md:p-4">
      <div className="bg-white  sm:rounded-2xl shadow-xl sm:shadow-2xl w-full max-w-[100vh] sm:max-w-[90vw] md:max-w-3xl max-h-[100vh] overflow-hidden mx-auto">
        <ModalHeader
          config={config}
          currentStep={currentStep}
          onClose={onClose}
        />

        <form
          onSubmit={handleSubmit}
          className="p-2 sm:p-4 md:p-4 overflow-y-auto"
        >
          <div className="space-y-4 sm:space-y-5 md:space-y-6">
            {currentFields.map((field) => (
              <FormField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={handleChange}
                fileRef={field.type === "file" ? fileInputRef : null}
              />
            ))}
          </div>

          <div
            className={`flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 sm:pt-6 mt-4 sm:mt-6 border-t border-gray-200 ${
              config.steps > 1 ? "justify-between" : "justify-end"
            }`}
          >
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep((s) => s - 1)}
                className="order-2 sm:order-1 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm: hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center text-sm sm:text-base"
              >
                Previous
              </button>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 order-1 sm:order-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm: hover:bg-gray-50 transition-colors font-semibold text-sm sm:text-base"
              >
                Cancel
              </button>

              {currentStep < config.steps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-lg sm: transition-colors font-semibold flex items-center justify-center text-sm sm:text-base"
                >
                  Continue
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white rounded-lg sm: hover:bg-green-700 disabled:bg-gray-400 transition-colors font-semibold flex items-center justify-center min-w-24 text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      <span className="text-xs sm:text-sm">Submitting...</span>
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const ModalHeader = ({ config, currentStep, onClose }) => (
  <div className="bg-gradient-to-r from-primary to-blue-600 p-4 sm:p-5 md:p-6 text-white">
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="flex items-center mb-2">
          <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-white/20 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
            <Ticket size={18} />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold leading-tight">
              {config.title}
            </h2>
            <p className="text-blue-100 text-xs sm:text-sm md:text-base mt-1 leading-relaxed">
              {config.subtitle}
            </p>
          </div>
        </div>
      </div>
      <button
        onClick={onClose}
        className="text-white/80 hover:text-white transition-colors p-1 sm:p-2 rounded-lg hover:bg-white/10 ml-2 flex-shrink-0"
      >
        <X className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
    </div>
  </div>
);

const FormField = ({ field, value, onChange, fileRef }) => {
  const Icon = field.icon;

  return (
    <div className="group">
      <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center">
        {Icon && (
          <Icon size={16} className="mr-2 text-gray-500 flex-shrink-0" />
        )}
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {field.description && (
        <p className="text-xs text-gray-500 mb-2 -mt-1">{field.description}</p>
      )}

      {field.type === "textarea" ? (
        <textarea
          name={field.name}
          value={value}
          onChange={onChange}
          rows={field.rows || 4}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm: focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          placeholder={field.placeholder}
          required={field.required}
        />
      ) : field.type === "select" ? (
        <select
          name={field.name}
          value={value}
          onChange={onChange}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm: focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
          required={field.required}
        >
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : field.type === "file" ? (
        <input
          ref={fileRef}
          type="file"
          name={field.name}
          onChange={onChange}
          accept={field.accept}
          className="w-full"
        />
      ) : (
        <input
          type={field.type}
          name={field.name}
          value={value}
          onChange={onChange}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm: focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          placeholder={field.placeholder}
          required={field.required}
        />
      )}
    </div>
  );
};

export default SupportModal;
